import {
  Minter_Type_Names_Enum,
  Projects_Metadata,
  Render_Types_Enum,
  SoloViewerOobTokenFragment,
  SoloViewerTokenFragment,
  Tokens_Metadata
} from '@/gql/generated/graphql'
import { OperationContext } from '@urql/core'
import { LilSquigIcon, OpenseaIcon } from 'design-configuration/components'
import { ONE_MILLION } from '../constants/common'

export function isValidEmail(email: string): boolean {
  const validEmailRegex = new RegExp('[a-z0-9]+@[a-z]+.[a-z]{2,3}')
  return validEmailRegex.test(email)
}

export const isVideoAsset = (assetUrl: string): boolean => !!assetUrl?.includes('mp4')
export const isGifAsset = (assetUrl: string): boolean => !!assetUrl?.includes('gif')
export const isVideoOrGifAsset = (assetUrl: string): boolean => isVideoAsset(assetUrl) || isGifAsset(assetUrl)
export const isImageAsset = (assetUrl: string): boolean => !!assetUrl?.includes('png')

export const getNonLiveDisplayType = (
  projectMetadata?: Pick<Projects_Metadata, 'primary_render_type' | 'preview_render_type'>,
  token?: Pick<Tokens_Metadata, 'video_id'>
): Render_Types_Enum => {
  if (!token || !projectMetadata) {
    return Render_Types_Enum.Image
  }
  if (
    (projectMetadata.primary_render_type === Render_Types_Enum.Video ||
      (projectMetadata.primary_render_type === Render_Types_Enum.Live &&
        projectMetadata.preview_render_type !== Render_Types_Enum.Image)) && // covers the case of accidental/unwanted video renders
    token.video_id
  ) {
    return Render_Types_Enum.Video
  }
  return Render_Types_Enum.Image
}

export const sliceAddressForDisplay = (publicAddress: string): string =>
  `${publicAddress.substring(0, 5)}...${publicAddress.substring(publicAddress.length - 4)}`

export type SoloViewerToken = SoloViewerTokenFragment | SoloViewerOobTokenFragment

export const getInvocationFromTokenId = (tokenId: string | number): number => {
  if (typeof tokenId === 'string') {
    tokenId = parseInt(tokenId)
  }
  return tokenId % ONE_MILLION
}

export const isValidDate = (date: Date): boolean => {
  if (date.toString() === 'Invalid Date') {
    console.error(`Error ${date} in format date`)
    return false
  }
  return true
}

// format date objects with timezone
export const DEFAULT_DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  timeZoneName: 'short'
}
export const formatDate = (date: Date, formatOptions?: Intl.DateTimeFormatOptions, locale?: string): string => {
  if (!isValidDate(date)) {
    return ''
  }

  return Intl.DateTimeFormat(locale ?? undefined, formatOptions ?? DEFAULT_DATE_FORMAT_OPTIONS).format(date)
}

export const getTimezone = (date: Date): string => {
  const timeZoneFormatter = Intl.DateTimeFormat(undefined, {
    timeZoneName: 'short'
  })

  return timeZoneFormatter.formatToParts(date).find((part) => part.type === 'timeZoneName')?.value ?? 'EST'
}

export const formatCompleteDate = (date: Date): string => {
  if (!isValidDate(date)) {
    return ''
  }
  const day = date.toLocaleString('default', { day: '2-digit' })
  const month = date.toLocaleString('default', { month: 'short' }).toUpperCase()

  return `${day} ${month}`
}

export const formatUpcomingDate = (date: Date): string => {
  if (!isValidDate(date)) {
    return ''
  }
  const day = date.toLocaleString('default', { day: '2-digit' })
  const month = date.toLocaleString('default', { month: 'short' })
  const year = date.toLocaleString('default', { year: 'numeric' })
  const time = date.toLocaleTimeString('default', {
    hour: 'numeric',
    minute: '2-digit'
  })

  return `${day} ${month} ${year} at ${time}`
}

export const formatTime = (num: number): string => {
  return num <= 9 ? `0${num}` : `${num}`
}

export const getTimeInSecondsBetweenDates = (date1: Date, date2: Date): number => {
  if (!isValidDate(date1) || !isValidDate(date2)) {
    console.error('Invalid date given to getTimeInSecondsBetweenDates')
    return -999
  }
  return Math.floor((date1.getTime() - date2.getTime()) / 1000)
}

// this function is used to create query state for a single filter value
export const createFilterQueryString = (filterName: string, filterOption: string): string => {
  const params = new URLSearchParams()
  params.set(filterName, filterOption)
  return params.toString()
}

// not to be used in place of Minter_Type_Names_Enum -- these enum will catch all versions
// of one particular minter (ie. MinterSetPriceV0, MinterSetPriceV1, etc)
export enum MinterType {
  setPriceETH = 'MinterSetPriceV',
  setPriceERC20 = 'MinterSetPriceERC20',
  DALinear = 'MinterDALin',
  DAExponential = 'MinterDAExp',
  merkle = 'MinterMerkle',
  holder = 'MinterHolder',
  DAExpSettlement = 'MinterDAExpSettlement',
  sea = 'MinterSEA',
  sharedMerkle = 'MinterSetPriceMerkleV',
  sharedHolder = 'MinterSetPriceHolderV',
  minPrice = 'MinterMinPriceV',
  minPriceMerkle = 'MinterMinPriceMerkleV',
  minterRAM = 'MinterRAM'
}

/**
 * Return whether or not the minter type exists in the `MinterType` enum
 * @param minterType minter type string to check
 * @returns boolean indicating whether or not the minter type is recognized
 */
export const isRecognizedMinterType = (minterType: Minter_Type_Names_Enum | undefined): boolean =>
  Object.values(MinterType).some((t) => !!minterType?.includes(t))

export const isSetPriceMinter = (minterType: Minter_Type_Names_Enum): boolean => {
  return (
    minterType.includes(MinterType.setPriceETH) ||
    minterType.includes(MinterType.setPriceERC20) ||
    minterType.includes(MinterType.minPrice)
  )
}

export const isMinPriceMinter = (minterType: Minter_Type_Names_Enum): boolean => {
  return minterType.includes(MinterType.minPrice) || minterType.includes(MinterType.minPriceMerkle)
}
export const isAuctionMinter = (minterType: Minter_Type_Names_Enum): boolean => {
  return minterType.includes(MinterType.DAExponential) || minterType.includes(MinterType.DALinear)
}
export const isGatedMinter = (minterType: Minter_Type_Names_Enum | undefined): boolean => {
  if (!minterType) {
    return false
  }

  return (
    minterType.includes(MinterType.merkle) ||
    minterType.includes(MinterType.sharedMerkle) ||
    minterType.includes(MinterType.holder) ||
    minterType?.includes(MinterType.sharedHolder) ||
    minterType.includes(MinterType.minPriceMerkle)
  )
}
export const getMinterName = (minterType?: Minter_Type_Names_Enum): string => {
  if (!isRecognizedMinterType(minterType) || !minterType) {
    return 'Unknown'
  }
  if (isSetPriceMinter(minterType)) {
    return 'Set price'
  } else if (isAuctionMinter(minterType)) {
    if (minterType.includes(MinterType.DAExpSettlement)) {
      return 'Exponential Dutch auction with settlement'
    } else if (minterType.includes(MinterType.DALinear)) {
      return 'Linear Dutch auction'
    } else if (minterType.includes(MinterType.DAExponential)) {
      return 'Exponential Dutch auction'
    }
  } else if (isGatedMinter(minterType)) {
    return 'Allowlist'
  } else if (minterType.includes(MinterType.sea)) {
    return 'Serial English auction'
  } else if (minterType.includes(MinterType.minterRAM)) {
    return 'Ranked auction'
  }
  return minterType
}

export const capitalizeFirstLetter = (str: string): string => {
  if (!str?.length) {
    return ''
  }
  return `${str.charAt(0).toUpperCase()}${str.substring(1, str.length).toLowerCase()}`
}

export const buildTweetUrl = (text: string, url: string): string => {
  return `https://twitter.com/intent/tweet?${buildTweetParams(text, url)}`
}

export const buildTweetParams = (text: string, url: string): URLSearchParams => {
  const tweetParams = new URLSearchParams()
  tweetParams.append('text', text)
  tweetParams.append('url', url)
  return tweetParams
}

export const createTagWithVal = (tag: string, val: string): string => {
  return `${tag}-${val}`
}

// Default to rely on on-demand revalidation
const DEFAULT_REVALIDATE = false

/**
 * Creates an URQL operation context object configured for Next.js caching.
 *
 * This function sets up the necessary configuration for server-side queries with proper
 * Next.js cache handling. It defaults to 'network-only' request policy since these queries
 * typically run on the server, and we want to leverage Next.js caching instead of URQL's
 * client-side cache.
 *
 * @param options - Configuration options for the URQL operation context
 * @param options.tags - Array of cache tags used for targeted revalidation with revalidateTag()
 * @param options.revalidate - Optional time in seconds to cache the response, defaults to DEFAULT_REVALIDATE (false meaning indefinite caching)
 * @param options.requestPolicy - Optional URQL request policy, defaults to 'network-only' to bypass URQL cache
 * @returns A partial OperationContext object with fetchOptions and requestPolicy configured.
 *
 * @example
 * // With default revalidation time and request policy
 * const context = createUrqlNextCacheContext({ tags: ['products', 'homepage'] });
 *
 * @example
 * // With custom revalidation time (5 minutes)
 * const context = createUrqlNextCacheContext({ tags: ['user-profile'], revalidate: 300 });
 *
 * @example
 * // With custom request policy
 * const context = createUrqlNextCacheContext({ tags: ['user-profile'], requestPolicy: 'cache-first' });
 */
export const createUrqlNextCacheContext = (options: {
  tags: string[]
  revalidate?: number
  requestPolicy?: 'cache-first' | 'cache-and-network' | 'network-only' | 'cache-only'
}): Partial<OperationContext> => {
  const { tags, revalidate, requestPolicy = 'network-only' } = options

  return {
    fetchOptions: { next: { tags, revalidate: revalidate ?? DEFAULT_REVALIDATE } },
    requestPolicy
  }
}

export const getPlatformIcon = (platform: string | undefined | null) => {
  if (!platform || !ALLOWED_LIST_PLATFORMS.includes(platform)) {
    return null
  }

  if (platform === 'OpenSea') {
    return OpenseaIcon
  }

  return LilSquigIcon
}

export const getOpenseaUrl = (contractAddress: string, tokenId: string): string => {
  return `https://opensea.io/item/ethereum/${contractAddress}/${tokenId}`
}

export const ALLOWED_LIST_PLATFORMS = ['OpenSea', 'artblocks.io', 'Sansa']

/**
 * Converts a token ID as represented on the contract to its corresponding project ID, also as represented on the contract.
 *
 * **Important:** This function deals with numeric IDs used directly by the smart contracts.
 * It should NOT be confused with the database IDs which typically have the format:
 *  - Database Project ID: `<contract-address>-<contract-project-id>`
 *  - Database Token ID: `<contract-address>-<contract-token-id>`
 *
 * @param tokenId - The numeric token ID from the contract (e.g., 123000456, representing project 123, invocation 456). Can be a string or number.
 * @returns The numeric project ID as a string (e.g., "123").
 *
 * @example
 * const contractTokenId = 123000456;
 * const contractProjectId = contractTokenIdToProjectId(contractTokenId); // Returns "123"
 *
 * // Contrast with typical database IDs:
 * // const dbProjectId = "0xabc...def-123";
 * // const dbTokenId = "0xabc...def-123000456";
 */
export function contractTokenIdToProjectId(tokenId: number | string): string {
  if (typeof tokenId === 'string') {
    tokenId = Number(tokenId)
  }

  return Math.floor(tokenId / 1_000_000).toString()
}

export function getRelativeTimeString(dateString: string): string {
  if (!dateString) return ''

  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()

  if (diffMs < 0) return 'Just now'

  // Convert to minutes
  const minutes = Math.floor(diffMs / (1000 * 60))
  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`

  // Convert to hours
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`

  // Convert to days
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`

  // Convert to months
  const months = Math.floor(days / 30)
  if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`

  // Convert to years
  const years = Math.floor(days / 365)
  return `${years} year${years === 1 ? '' : 's'} ago`
}

export function generateRandomTokenData(
  projectIndex: number,
  maxInvocations: number
): { hash: string; tokenId: string } {
  let hash = '0x'
  for (let i = 0; i < 64; i++) {
    hash += Math.floor(Math.random() * 16).toString(16)
  }

  return {
    hash,
    tokenId: `${ONE_MILLION * projectIndex + Math.floor(Math.random() * maxInvocations)}`
  }
}

export const ab500ArticleSlug = 'art-blocks-500-complete-collection'
